import React, {useEffect, useState} from 'react';
import {StyleSheet, TouchableOpacity, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCText from '../CCText';
import {UndoBannerState} from '@/Hooks/ManageMoves/useMoveTreeUndo';

function getPlayedText(activeMove: any): string {
  const san = activeMove?.san || activeMove?.move;
  if (!san) return 'Starting position';
  const fen = activeMove?.fen || activeMove?.newFen;
  const activeColor = typeof fen === 'string' ? fen.split(' ')[1] : undefined;
  // `fen` is the position *after* this move, so if it's black to move next,
  // this move was White's.
  const colorLabel =
    activeColor === 'b' ? 'White' : activeColor === 'w' ? 'Black' : '';
  return colorLabel ? `${colorLabel} played ${san}` : `Played ${san}`;
}

function iconFor(bannerType?: 'delete' | 'promote'): string {
  if (bannerType === 'delete') return 'trash-outline';
  if (bannerType === 'promote') return 'arrow-up-circle-outline';
  return 'information-circle-outline';
}

/**
 * Fixed status strip right below the move search bar: continuously reflects
 * the current move (or, while a promote/delete undo is pending, that event
 * instead), with Undo pinned to the right end when there's something to undo.
 */
export default function MoveUpdateBar({
  undoBanner,
  onUndo,
  activeMove,
}: {
  undoBanner: UndoBannerState | null;
  onUndo: () => void;
  activeMove: any;
}) {
  const [remainingPct, setRemainingPct] = useState(100);
  const [remainingSec, setRemainingSec] = useState(0);

  useEffect(() => {
    if (!undoBanner) return undefined;
    const start = Date.now();
    const total = Math.max(1, undoBanner.expiresAt - start);
    const tick = () => {
      const diff = undoBanner.expiresAt - Date.now();
      const pct = Math.max(
        0,
        Math.min(100, (diff / total) * 100),
      );
      setRemainingPct(pct);
      setRemainingSec(Math.max(0, Math.ceil(diff / 1000)));
    };
    tick();
    const interval = setInterval(tick, 200);
    return () => clearInterval(interval);
  }, [undoBanner?.expiresAt]);

  const message = undoBanner ? undoBanner.message : getPlayedText(activeMove);

  return (
    <View style={styles.wrapper}>
      <View style={styles.row}>
        <Ionicons
          name={iconFor(undoBanner?.type)}
          size={14}
          color="#6b584d"
        />
        <CCText
          fontSize={11}
          color="#6b584d"
          numberOfLines={1}
          style={styles.message}>
          {message}
          {undoBanner && (
            <CCText style={styles.dullText}>
              {` (cannot undo after ${remainingSec} seconds)`}
            </CCText>
          )}
        </CCText>
        {undoBanner && (
          <TouchableOpacity
            onPress={onUndo}
            activeOpacity={0.7}
            style={styles.undoButton}>
            <CCText style={styles.undoText}>Undo</CCText>
          </TouchableOpacity>
        )}
      </View>
      {undoBanner && (
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, {width: `${remainingPct}%`}]} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    backgroundColor: '#FAF6EB',
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
    paddingHorizontal: 10,
    gap: 6,
  },
  message: {
    flex: 1,
  },
  dullText: {
    color: '#A8A7A6',
    fontSize: 12,
  },
  undoButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginLeft: 4,
  },
  undoText: {
    color: '#CD2C39',
    fontSize: 12,
    fontWeight: '700',
  },
  progressTrack: {
    height: 2,
    backgroundColor: 'rgba(38,35,34,0.08)',
  },
  progressFill: {
    height: 2,
    backgroundColor: '#CD2C39',
  },
});
